import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Scan, Check, X, Crown, Users, Zap, Building2, ArrowRight } from 'lucide-react';

export default function Precios({ onBack, onNavigateAuth, onNavigateProfile, user }) {
  const [billing, setBilling] = useState('monthly');

  const plans = [
    {
      id: 'free',
      name: 'Gratuito',
      icon: <Zap className="w-5 h-5" />,
      price: { monthly: 0, yearly: 0 },
      color: 'border-zinc-700',
      badge: null,
      description: 'Para explorar y uso ocasional.',
      cta: user ? 'Plan actual' : 'Empezar gratis',
      ctaStyle: 'border border-zinc-600 hover:border-zinc-400 text-zinc-300',
      features: [
        { text: '5 análisis diarios por dispositivo', ok: true },
        { text: 'Imágenes, texto, PDF y URLs', ok: true },
        { text: 'Sin necesidad de registro', ok: true },
        { text: 'Historial local en el navegador', ok: true },
        { text: 'Análisis de vídeo (MP4, MOV...)', ok: false },
        { text: 'Historial en la nube', ok: false },
        { text: 'Exportar informes PDF', ok: false },
        { text: 'Análisis por lotes', ok: false },
        { text: 'API pública', ok: false },
      ],
    },
    {
      id: 'pro',
      name: 'Pro',
      icon: <Crown className="w-5 h-5" />,
      price: { monthly: 4.99, yearly: 3.99 },
      color: 'border-blue-500',
      badge: 'Más popular',
      badgeColor: 'bg-blue-500',
      description: 'Para profesionales y uso intensivo.',
      cta: user?.plan === 'pro' ? 'Plan actual' : 'Empezar Pro',
      ctaStyle: 'bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_30px_rgba(59,130,246,0.3)]',
      features: [
        { text: 'Análisis ilimitados', ok: true },
        { text: 'Imágenes, vídeo, texto, PDF y URLs', ok: true },
        { text: 'Análisis de vídeo (MP4, MOV, AVI, WebM, MKV)', ok: true },
        { text: 'Historial en la nube', ok: true },
        { text: 'Exportar informes PDF', ok: true },
        { text: 'Análisis por lotes (hasta 10)', ok: true },
        { text: 'API pública — 500 llamadas/día', ok: true },
        { text: 'Prioridad en cola de análisis', ok: true },
      ],
    },
    {
      id: 'business',
      name: 'Business',
      icon: <Building2 className="w-5 h-5" />,
      price: { monthly: 29, yearly: 23 },
      color: 'border-purple-500',
      badge: 'Empresas',
      badgeColor: 'bg-purple-500',
      description: 'Para equipos y empresas con volumen alto.',
      cta: user?.plan === 'business' ? 'Plan actual' : 'Empezar Business',
      ctaStyle: 'bg-purple-600 hover:bg-purple-500 text-white',
      features: [
        { text: 'Todo lo del plan Pro', ok: true },
        { text: '5 usuarios incluidos — invita a tu equipo', ok: true },
        { text: 'Análisis por lotes (hasta 50)', ok: true },
        { text: 'API — 5.000 llamadas/día', ok: true },
        { text: 'Soporte prioritario por email', ok: true },
        { text: 'Factura para empresa', ok: true },
        { text: 'Panel de equipo (próximamente)', ok: true },
        { text: 'SLA garantizado (próximamente)', ok: true },
      ],
    },
  ];

  const faqs = [
    { q: '¿Cuántos análisis incluye el plan gratuito?', a: 'El plan gratuito incluye 5 análisis diarios por dispositivo (navegador). El límite es independiente por cada dispositivo y navegador que uses, por lo que distintos dispositivos de casa tienen su propia cuota.' },
    { q: '¿Puedo cancelar en cualquier momento?', a: 'Sí. Puedes cancelar tu suscripción Pro o Business desde tu perfil en cualquier momento. La cancelación es efectiva al final del período de facturación actual.' },
    { q: '¿Hay reembolsos?', a: 'No realizamos reembolsos por períodos parciales ya facturados, salvo lo exigido por la legislación aplicable.' },
    { q: '¿Cómo funciona el plan anual?', a: 'Al elegir facturación anual pagas 12 meses por adelantado con un 20% de descuento respecto al precio mensual.' },
    { q: '¿Puedo cambiar de plan?', a: 'Sí, puedes subir o bajar de plan en cualquier momento desde tu perfil. Los cambios se aplican inmediatamente.' },
    { q: '¿Qué formatos de vídeo se admiten?', a: 'Soportamos MP4, MOV, AVI, WebM y MKV. El vídeo se analiza fotograma a fotograma con Gemini 2.5 Flash buscando deepfakes y artefactos de IA. El tamaño máximo recomendado es 500 MB.' },
    { q: '¿La API tiene límites?', a: 'En el plan Pro puedes hacer hasta 500 llamadas/día a la API. En Business hasta 5.000 llamadas/día. Contáctanos si necesitas más.' },
    { q: '¿Qué métodos de pago aceptáis?', a: 'Aceptamos todas las tarjetas de crédito y débito principales (Visa, Mastercard, American Express) a través de Stripe. El pago es seguro y cifrado.' },
    { q: '¿Qué modelo de IA usa Veritas Scan?', a: 'Usamos Gemini 2.5 Flash de Google DeepMind, un modelo multimodal de última generación capaz de analizar imágenes, vídeos y texto con razonamiento profundo y sesgo escéptico.' },
  ];

  return (
    <div className="min-h-screen bg-zinc-950 relative">
      <div className="scanlines" />
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 0%, rgba(59, 130, 246, 0.08), transparent 60%)' }} />

      <header className="relative z-10 border-b border-zinc-800 sticky top-0 bg-zinc-950/90 backdrop-blur-xl z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3">
          <button onClick={onBack} className="text-zinc-400 hover:text-white transition-colors font-mono text-xs sm:text-sm flex-shrink-0">← <span className="hidden sm:inline">Volver</span></button>
          <div className="flex items-center gap-2 sm:gap-3">
            <Scan className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
            <span className="font-heading text-base sm:text-xl font-black">VERITAS <span className="text-blue-500">SCAN</span></span>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16 space-y-14 sm:space-y-20">

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center space-y-4">
          <p className="font-mono text-xs tracking-widest text-blue-500 uppercase">Planes y precios</p>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black tracking-tight">Simple y transparente</h1>
          <p className="text-zinc-400 text-base sm:text-xl max-w-xl mx-auto">Sin sorpresas. Cancela cuando quieras.</p>

          <div className="flex items-center justify-center gap-3 mt-6">
            <span className={`font-mono text-sm ${billing === 'monthly' ? 'text-white' : 'text-zinc-500'}`}>Mensual</span>
            <button
              onClick={() => setBilling(b => b === 'monthly' ? 'yearly' : 'monthly')}
              className={`relative w-12 h-6 rounded-full transition-colors ${billing === 'yearly' ? 'bg-blue-600' : 'bg-zinc-700'}`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${billing === 'yearly' ? 'translate-x-7' : 'translate-x-1'}`} />
            </button>
            <span className={`font-mono text-sm ${billing === 'yearly' ? 'text-white' : 'text-zinc-500'}`}>
              Anual <span className="text-emerald-400 text-xs">−20%</span>
            </span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className={`relative bg-zinc-900 border-2 ${plan.color} rounded-2xl p-5 sm:p-7 flex flex-col gap-5 sm:gap-6 ${plan.id === 'pro' ? 'sm:-mt-4 sm:mb-4' : ''}`}
            >
              {plan.badge && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 ${plan.badgeColor} text-white font-mono text-xs uppercase tracking-widest rounded-full`}>
                  {plan.badge}
                </div>
              )}

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className={plan.id === 'pro' ? 'text-blue-400' : plan.id === 'business' ? 'text-purple-400' : 'text-zinc-400'}>
                    {plan.icon}
                  </span>
                  <h2 className="font-heading text-xl font-bold">{plan.name}</h2>
                </div>
                <div className="mb-2">
                  {plan.price.monthly === 0 ? (
                    <span className="font-heading text-4xl font-black">Gratis</span>
                  ) : (
                    <div className="flex items-end gap-1">
                      <span className="font-heading text-4xl font-black">
                        ${billing === 'yearly' ? plan.price.yearly : plan.price.monthly}
                      </span>
                      <span className="text-zinc-500 font-mono text-sm mb-1">/mes</span>
                    </div>
                  )}
                  {billing === 'yearly' && plan.price.monthly > 0 && (
                    <p className="text-emerald-400 text-xs font-mono mt-1">
                      Facturado ${(plan.price.yearly * 12).toFixed(0)}/año
                    </p>
                  )}
                </div>
                <p className="text-zinc-500 text-sm">{plan.description}</p>
              </div>

              <button
                onClick={async () => {
                  if (plan.id === 'free') { onBack(); return; }
                  if (user?.plan === plan.id) { onNavigateProfile(); return; }
                  if (!user) { onNavigateAuth(); return; }
                  try {
                    const token = localStorage.getItem('veritas_token');
                    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://veritas-scan-production.up.railway.app';
                    const res = await fetch(`${backendUrl}/api/stripe/create-checkout`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ plan: plan.id, billing })
                    });
                    const data = await res.json();
                    if (!res.ok) { alert('Error: ' + (data.detail || JSON.stringify(data))); return; }
                    if (data.url) window.location.href = data.url;
                  } catch (e) {
                    alert('Error de red: ' + (e?.message || 'desconocido'));
                  }
                }}
                disabled={(plan.id === 'free' && !!user) || user?.plan === plan.id}
                className={`w-full py-3 rounded-xl font-mono text-sm uppercase tracking-wider transition-all flex items-center justify-center gap-2 ${plan.ctaStyle} disabled:opacity-50 disabled:cursor-default`}
              >
                {plan.cta} {plan.id !== 'free' && user?.plan !== plan.id && <ArrowRight className="w-4 h-4" />}
              </button>

              <ul className="space-y-3 flex-1">
                {plan.features.map((f, j) => (
                  <li key={j} className={`flex items-start gap-3 text-sm ${f.ok ? 'text-zinc-300' : 'text-zinc-600'}`}>
                    {f.ok
                      ? <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      : <X className="w-4 h-4 text-zinc-700 flex-shrink-0 mt-0.5" />
                    }
                    {f.text}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 sm:p-6 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
          <Users className="w-8 h-8 text-purple-400 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-heading font-bold text-white mb-1">¿Necesitas más de 5 usuarios o volumen personalizado?</h3>
            <p className="text-zinc-400 text-sm">Contáctanos para un plan Enterprise personalizado con integraciones, SLA y facturación a medida.</p>
          </div>
          <a href="mailto:business@veritas-scan.com?subject=Plan Enterprise"
            className="flex-shrink-0 px-5 py-2 bg-purple-600 hover:bg-purple-500 text-white font-mono text-sm uppercase tracking-wider rounded-lg transition-all">
            Contactar
          </a>
        </motion.div>

        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-black text-center mb-8 sm:mb-10">Preguntas frecuentes</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} viewport={{ once: true }}
                className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-2">
                <h3 className="font-heading font-bold text-white text-sm">{faq.q}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>

      </main>
    </div>
  );
}
